<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { ButtonQC } from '@qicgroupdoha/qic-connect-ds';
import BaseButton from '~/libs/shared/ui/src/components/buttons/BaseButton/BaseButton.vue';
import { BasePopup } from '@shared/ui';
import { useRtl, useUrl } from '@shared/composables';
import type { LinkItem, Media, Modal } from '~/types/api';
import { LOCAL_STORAGE_KEY } from '~/libs/shared/constants/src/common';

const { isRtl } = useRtl();
const isModal = ref(false);
const modalData = ref<any>(null);
const { locale } = useI18n();
const { assignUrl } = useUrl();

interface Article {
  badge_title: string;
  badge_icon: Media;
  title: string;
  description: string;
  link_button: LinkItem;
  modal: Modal;
  modal_button: string;
  car_insurance_entrypoint: string;
  image_desktop: any;
  image_mobile: any;
}

const props = defineProps<{
  articles: Article[];
  description: string;
  title: string;
  bottom_link: LinkItem;
}>();

function articleAction (article: Article) {
  const baseFlowPath = `/${locale.value}/car-insurance/flow/phone`;
  localStorage.setItem(LOCAL_STORAGE_KEY.ENTRY_TYPE, 'auto');
  localStorage.setItem(LOCAL_STORAGE_KEY.ENTRY_POINT, article.car_insurance_entrypoint);
  assignUrl(`${baseFlowPath}?redirectURL=${window.location.host}${window.location.pathname}`, {}, true);
}

function showModal (data: any) {
  modalData.value = data;
  isModal.value = true;
}

function closeModal () {
  modalData.value = null;
  isModal.value = false;
}

function onActionBtn (href: string) {
  window.open(href);
}
</script>

<template>
  <section class="documents section-offset" dir="auto">
    <div class="container">
      <div class="documents__head">
        <h3 v-if="props?.title">
          {{ props.title }}
        </h3>
        <p v-if="props?.description">
          {{ props.description }}
        </p>
      </div>
      <div class="documents__scroll">
        <div class="documents__wrapper">
          <div v-for="article in articles" class="documents__article">
            <div class="documents__article-img">
              <img
                v-if="article?.image_desktop?.url" class="is_desktop"
                :src="article.image_desktop.url" :alt="article?.title" fetchpriority="low"
              >
              <img
                v-if="article?.image_mobile?.url" class="is_mobile"
                :src="article.image_mobile.url" :alt="article?.title" fetchpriority="low"
              >
            </div>
            <div class="documents__content">
              <div v-if="article?.badge_title" class="documents__badge">
                <img
                  v-if="article?.badge_icon?.url" :src="article.badge_icon.url" :alt="article?.badge_title"
                  fetchpriority="low"
                >
                {{ article?.badge_title }}
              </div>
              <h3 class="documents__title">
                {{ article?.title }}
              </h3>
              <p v-if="article?.description" class="documents__description">
                {{ article.description }}
              </p>
              <ButtonQC
                v-if="article?.link_button?.title"
                icon="none" class="documents__check"
                @click="articleAction(article)"
              >
                <!-- <img :src="article?.link_button?.icon?.url" :alt="props?.content?.form_button_text"> -->
                {{ article?.link_button?.title }}
                <!-- <img :src="props?.content?.form_button_icon?.url" :alt="props?.content?.form_button_text"> -->
              </ButtonQC>
              <button
                v-if="article?.modal_button && article?.modal" class="documents__faq btn dc-link-button"
                @click.prevent="showModal(article?.modal)"
              >
                {{ article?.modal_button }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <ButtonQC
        v-if="props?.bottom_link?.href"
        icon="none"
        size="medium"
        variant="outline"
        class="documents__compare"
        @click="onActionBtn(props?.bottom_link?.href)"
      >
        <img
          v-if="props?.bottom_link?.icon_position === 'left'"
          :src="props?.bottom_link?.icon?.url"
          :alt="props?.bottom_link?.title"
        >
        {{ props?.bottom_link?.title }}
        <img
          v-if="props?.bottom_link?.icon_position === 'right'"
          :src="props?.bottom_link?.icon?.url"
          :alt="props?.bottom_link?.title"
        >
      </ButtonQC>
    </div>

    <!-- TODO: add common plugin for opening modals -->
    <BasePopup
      v-if="isModal"
      big
      :popup-v2="true"
      :is-rtl="isRtl"
      :use-button="true"
      @close="closeModal"
    >
      <template #header>
        <div class="popup__header-title">
          <h4>{{ modalData?.title }}</h4>
          <div class="subtitle">
            {{ modalData?.description }}
          </div>
        </div>
        <div v-if="modalData?.image?.url" class="popup__header-picture">
          <img :src="modalData.image.url" alt="">
        </div>
      </template>

      <div class="landing-includes__popup">
        <h5>{{ modalData?.subtitle }}</h5>
        <div class="content" v-html="modalData?.content" />
      </div>

      <template #button>
        <div class="landing-includes__popup__footer">
          <ButtonQC
            icon="none"
            size="medium"
            variant="outline"
            class="landing-includes__popup__btn"
            @click="closeModal"
          >
            <span>{{ $t('main.includes.close.button') }}</span>
          </ButtonQC>
        </div>
      </template>
    </BasePopup>
  </section>
</template>

<style lang="scss" scoped>
@import './QuoteCalculator.scss';
</style>
